/**
 * apiFetch — single entry point for every backend call.
 *
 * 1:1 port of legacy main.js apiFetch / apiState (lines 89–463). Hard rules:
 *  - Authorization header injection (Bearer <token>) is automatic when `auth: true`.
 *  - `apiState.online` is a 3-state machine (`null` = unknown, `true` = online,
 *    `false` = offline / fallback), updated on every request.
 *  - On 401 the token is cleared and the error is rethrown — UI layer (AuthSheet)
 *    is responsible for prompting login. Never silently swallow auth failures.
 *  - On network failure, `apiState.online` flips to `false` and the error is rethrown.
 *
 * Migration plan: docs/react-migration-plan.md §6.4.
 */

import { readStoredToken, storeToken } from "./auth.js";
import { ApiError, toApiError } from "./errors.js";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type ApiFetchOptions = {
  method?: HttpMethod;
  /** Inject Authorization: Bearer <token> if a token exists. */
  auth?: boolean;
  /** JSON-serializable body. If a FormData / Blob is needed, pass `rawBody`. */
  body?: unknown;
  /** Pre-built request body (FormData / Blob). Bypasses JSON serialization. */
  rawBody?: BodyInit;
  /** Extra headers merged on top of defaults. */
  headers?: Record<string, string>;
  /** AbortSignal forwarded to fetch. */
  signal?: AbortSignal;
  /** Override base URL (used by tests). Default: import.meta.env.VITE_API_BASE. */
  baseUrl?: string;
};

export type ApiState = {
  token: string | null;
  /** `null` = not yet probed, `true` = last request ok, `false` = last request failed. */
  online: boolean | null;
};

export const apiState: ApiState = {
  token: readStoredToken(),
  online: null,
};

function defaultBaseUrl(): string {
  const fromEnv = (import.meta.env.VITE_API_BASE as string | undefined)?.trim();
  return fromEnv && fromEnv.length > 0 ? fromEnv : "https://api.nsnzd.cn";
}

function joinUrl(baseUrl: string, path: string): string {
  // Absolute URL or protocol-relative — leave untouched.
  if (/^https?:\/\//i.test(path) || path.startsWith("//")) return path;
  const base = baseUrl.replace(/\/+$/, "");
  const tail = path.startsWith("/") ? path : `/${path}`;
  return `${base}${tail}`;
}

export function apiBaseUrl(): string {
  return defaultBaseUrl();
}

export function resolveApiUrl(path: string, baseUrl = defaultBaseUrl()): string {
  return joinUrl(baseUrl, path);
}

function compatibleAbortSignal(signal?: AbortSignal): AbortSignal | undefined {
  if (!signal || typeof Request === "undefined") return signal;
  try {
    new Request("https://tree-hole.local/__abort_signal_probe__", { signal });
    return signal;
  } catch {
    return undefined;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { method = "GET", auth = false, body, rawBody, headers, signal, baseUrl } = options;

  const finalHeaders = new Headers(headers);
  finalHeaders.set("Accept", "application/json");

  let finalBody: BodyInit | undefined;
  if (rawBody !== undefined) {
    finalBody = rawBody;
  } else if (body !== undefined) {
    finalHeaders.set("Content-Type", "application/json");
    finalBody = JSON.stringify(body);
  }

  if (auth && apiState.token) {
    finalHeaders.set("Authorization", `Bearer ${apiState.token}`);
  }

  const url = joinUrl(baseUrl ?? defaultBaseUrl(), path);
  const signalForFetch = compatibleAbortSignal(signal);

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: finalHeaders,
      body: finalBody,
      signal: signalForFetch,
      credentials: "omit",
    });
  } catch (err) {
    apiState.online = false;
    if (err instanceof ApiError) throw err;
    const msg = err instanceof Error ? err.message : "network error";
    throw new ApiError(msg, 0, "network");
  }

  apiState.online = res.ok || (res.status >= 400 && res.status < 600);

  if (!res.ok) {
    const apiErr = await toApiError(res);
    if (apiErr.status === 401) {
      // Token is no longer valid — clear it. UI layer decides whether to open AuthSheet.
      apiState.token = null;
      storeToken(null);
    }
    throw apiErr;
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

/** Update in-memory token and persist. Use AuthProvider in production code. */
export function setApiToken(token: string | null): void {
  apiState.token = token;
  storeToken(token);
}

/** Reset for tests. NOT exported through index.ts. */
export function __resetApiStateForTests(): void {
  apiState.token = null;
  apiState.online = null;
}
